import { CommonModule } from '@angular/common';
import { Component, computed, Input, signal, Signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FeatureFlag } from '@app/enums';
import { makeCurrencyMask } from '@app/functions';
import { TaxCredit } from '@app/models/employee/payroll/tax-credit.model';
import {
    TAX_CREDIT_DEFAULT_PERSONAL_AMOUNTS,
    TAX_CREDIT_DEFAULT_PERSONAL_YEARS,
    TaxCreditYear,
} from '@app/models/employee/payroll/types';
import { BaseForm } from '@app/modules/forms/base.form';
import { PlatformModule } from '@app/platform.module';
import { FeatureService } from '@app/services/feature.service';
import { ALL_PROVINCES, Province } from '@app/types/province';
import { Locale, TranslatableKey } from '@app/types/translatable.type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getYear } from 'date-fns';
import { conformToMask } from 'text-mask-core/dist/textMaskCore';
import { TaxCreditFormValidator } from './tax-credit-form.validator';

@Component({
    templateUrl: './tax-credit-form.template.html',
    selector: 'app-tax-credit-form',
    styleUrls: ['./tax-credit-form.style.scss'],
    standalone: true,
    imports: [TranslateModule, CommonModule, PlatformModule, FormsModule, TaxCreditFormValidator],
})
export class TaxCreditForm extends BaseForm {
    @Input() taxCredit: TaxCredit = new TaxCredit();

    currencyMask = makeCurrencyMask({ allowDecimal: true }, this.translateService.currentLang as Locale);
    jurisdictions: { label: TranslatableKey; value: Province }[] = [];

    readonly currentYear = getYear(new Date());
    // Only display years that have rates populated, greater than or equal to the current year
    readonly YEAR_RANGES = TAX_CREDIT_DEFAULT_PERSONAL_YEARS.filter((year) => year >= this.currentYear);

    readonly TD1_LINE_NUMBER: Record<Province | 'Canada', { line: number; form: string }> = {
        Canada: { line: 13, form: 'TD1' },
        AB: { line: 11, form: 'TD1' },
        BC: { line: 11, form: 'TD1' },
        MB: { line: 13, form: 'TD1' },
        NB: { line: 12, form: 'TD1' },
        NL: { line: 12, form: 'TD1' },
        NS: { line: 12, form: 'TD1' },
        NT: { line: 12, form: 'TD1' },
        NU: { line: 12, form: 'TD1' },
        ON: { line: 10, form: 'TD1' },
        PE: { line: 12, form: 'TD1' },
        SK: { line: 13, form: 'TD1' },
        YT: { line: 13, form: 'TD1' },
        QC: { line: 10, form: 'Form TP-1015.3' },
    };

    selectedYear: WritableSignal<TaxCreditYear | undefined> = signal(undefined);
    selectedJurisdiction: WritableSignal<Province | 'Canada' | undefined> = signal(undefined);

    /**
     * Retrieves the default tax rate to be shown as the placeholder in a field
     */
    PERSONAL_AMOUNT: Signal<string> = computed(() => {
        // Year must be selected by the user
        const selectedYear = this.selectedYear();
        if (!selectedYear || !TAX_CREDIT_DEFAULT_PERSONAL_AMOUNTS[selectedYear]) {
            return '';
        }
        // Jurisdiction can either be selected (provincial) or provided in advance on the TaxCredit model (federal)
        const selectedJurisdiction = this.selectedJurisdiction() ?? this.taxCredit.jurisdiction;
        if (!selectedJurisdiction || !TAX_CREDIT_DEFAULT_PERSONAL_AMOUNTS[selectedYear][selectedJurisdiction]) {
            return '';
        }

        return this.maskNumber(TAX_CREDIT_DEFAULT_PERSONAL_AMOUNTS[selectedYear][selectedJurisdiction]);
    });

    constructor(
        private translateService: TranslateService,
        private featureService: FeatureService
    ) {
        super();
        this.setJurisdictions();
    }

    clearYearAndJurisdictionSelections(): void {
        this.selectedJurisdiction.set(undefined);
        this.selectedYear.set(undefined);
    }

    private maskNumber(value: number): string {
        return conformToMask(value.toString(), this.currencyMask, {}).conformedValue;
    }

    private async setJurisdictions(): Promise<void> {
        // Only include QC if the feature-flag is set
        // Once the feature is released, this function can be deleted and the jurisdictions property can become a readonly constant
        const hasQuebec = await this.featureService.has(FeatureFlag.qcPayroll);
        this.jurisdictions = ALL_PROVINCES.filter((province) => hasQuebec || province !== 'QC').map((province) => ({
            label: `provinces.${province}`,
            value: province,
        }));
    }

    get isFederal(): boolean {
        return this.taxCredit.jurisdiction === 'Canada';
    }

    get isQuebec(): boolean {
        return this.taxCredit.jurisdiction === 'QC';
    }
}
